import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiPencil, HiTrash, HiEye, HiCheckCircle, HiBan } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../components/modern";
import type { Column, DateRangeValue } from "../../components/modern";
import { hasPermission } from "../../utils/permissions";
import { useAuth } from "../../contexts/AuthContext";
import { adminApi } from "../../utils/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastNotifications";
import './CustomerIndex.scss';

interface Customer {
  id: number;
  mailboxNumber: number;
  name: string;
  email: string;
  phone: string;
  verifiedAt: string;
  status: number;
  city: {
    id: number;
    name: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export const CustomerIndex: React.FC = () => {
  const { t, i18n } = useTranslation(['customers', 'common']);
  const navigate = useNavigate();
  const { token } = useAuth();
  const isRTL = i18n.language === 'ar';

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [dateFilters, setDateFilters] = useState<Record<string, DateRangeValue>>({});

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    const numericKeys = new Set(['mailboxNumber', 'status']);

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;

      if (numericKeys.has(key)) {
        const parsed = Number(trimmed.replace(/\D/g, ''));
        if (Number.isNaN(parsed)) return;
        params.append(key, parsed.toString());
        return;
      }

      params.append(key, trimmed);
    });

    Object.entries(dateFilters).forEach(([key, range]) => {
      if (range.from) params.append(`${key}From`, range.from);
      if (range.to) params.append(`${key}To`, range.to);
    });

    return params.toString();
  };

  const fetchCustomers = async (page: number, size: number) => {
    try {
      setLoading(true);
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/customer?${query}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('customers:messages.fetchError'));
        return;
      }

      setCustomers(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error) {
      showErrorToast(t('customers:messages.fetchError'));
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers(currentPage, pageSize);
  }, [currentPage, pageSize, filters, dateFilters]);

  // Export customers to Excel
  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/customer/export`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export customers');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Customers_Export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting customers:', error);
      alert('Failed to export customers. Please try again.');
    }
  };

  const toggleStatus = async (id: number, newStatus: number) => {
    const action = newStatus === 0 ? "activate" : "ban";
    if (!window.confirm(t('customers:messages.statusConfirm', { action }))) return;

    try {
      const response = await adminApi.call('/customer/updatecustomerstatus', 'POST', { id: id, userStatus: newStatus });

      if (!response.success) {
        showErrorToast(response.error || t('customers:messages.statusError'));
        return;
      }

      showSuccessToast(t('customers:messages.statusSuccess', { action }));
      fetchCustomers(currentPage, pageSize);
    } catch (error) {
      showErrorToast(t('customers:messages.statusError'));
      console.error('Error updating customer status:', error);
    }
  };

  const deleteCustomer = async (id: number) => {
    if (!window.confirm(t('customers:messages.deleteConfirm'))) return;

    try {
      const response = await adminApi.call(`/customer/${id}`, 'DELETE');

      if (!response.success) {
        showErrorToast(response.error || t('customers:messages.deleteError'));
        return;
      }

      showSuccessToast(t('customers:messages.deleteSuccess'));
      fetchCustomers(currentPage, pageSize);
    } catch (error) {
      showErrorToast(t('customers:messages.deleteError'));
      console.error('Error deleting customer:', error);
    }
  };

  const handleCreateCustomer = () => {
    navigate('/customers/create');
  };

  const handleEditCustomer = (id: number) => {
    navigate(`/customers/edit/${id}`);
  };

  const handleViewCustomer = (id: number) => {
    navigate(`/customers/${id}`);
  };

  const getStatusBadge = (status: number) => {
    switch (status) {
      case 0:
        return <span className="badge bg-success">{t('customers:statusOptions.active')}</span>;
      case 1:
        return <span className="badge bg-warning">{t('customers:statusOptions.inactive')}</span>;
      case 2:
        return <span className="badge bg-danger">{t('customers:statusOptions.suspended')}</span>;
      default:
        return <span className="badge bg-secondary">Unknown</span>;
    }
  };

  const columns: Column[] = [
    {
      key: 'mailboxNumber',
      title: t('customers:table.mailboxNumber'),
      sortable: true,
      filterable: true,
      filterKey: 'mailboxNumber',
      width: '120px',
    },
    {
      key: 'name',
      title: t('customers:table.name'),
      sortable: true,
      filterable: true,
      filterKey: 'name',
      render: (value) => <span>{value}</span>,
      width: '180px'
    },
    {
      key: 'email',
      title: t('customers:table.email'),
      sortable: true,
      filterable: true,
      filterKey: 'email',
    },
    {
      key: 'phone',
      title: t('customers:table.phone'),
      sortable: true,
      filterable: true,
      filterKey: 'phone',
    },
    {
      key: 'createdAt',
      title: t('customers:fields.verifiedAt'),
      sortable: true,
      filterable: true,
      filterKey: 'createdAt',
      filterType: 'dateRange',
      filterValue: (row) => row.verifiedAt ? new Date(row.verifiedAt).toLocaleDateString() : '-',
      render: (value) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      key: 'city',
      title: t('customers:table.city'),
      sortable: true,
      filterable: true,
      filterKey: 'city',
      filterValue: (row) => row.city || '-',
      render: (value) => value || '-',
    },
    {
      key: 'status',
      title: t('customers:table.status'),
      sortable: true,
      filterable: true,
      filterKey: 'status',
      filterValue: (row) => {
        switch (row.status) {
          case 0: return t('customers:statusOptions.active');
          case 1: return t('customers:statusOptions.inactive');
          case 2: return t('customers:statusOptions.suspended');
          default: return 'Unknown';
        }
      },
      render: (value) => getStatusBadge(value),
    },
    {
      key: 'actions',
      title: t('customers:table.actions'),
      sortable: false,
      width: '200px',
      align: 'center',
      render: (_, customer) => (
        <div className="action-buttons">
          {hasPermission("View Customer") && (
            <ActionButton
              variant="primary"
              size="sm"
              icon={HiEye}
              onClick={() => handleViewCustomer(customer.id)}
              tooltip={t('customers:actions.view')}
            />
          )}
          {hasPermission("Update Customer") && (
            <>
              <ActionButton
                variant="warning"
                size="sm"
                icon={HiPencil}
                onClick={() => handleEditCustomer(customer.id)}
                tooltip={t('customers:editCustomer')}
              />
              <ActionButton
                variant={customer.status === 0 ? "warning" : "success"}
                size="sm"
                icon={customer.status === 0 ? HiBan : HiCheckCircle}
                onClick={() => toggleStatus(customer.id, customer.status === 0 ? 2 : 0)}
                tooltip={customer.status === 0 ? t('customers:banCustomer') : t('customers:activateCustomer')}
              />
            </>
          )}
          {hasPermission("Delete Customer") && (
            <ActionButton
              variant="danger"
              size="sm"
              icon={HiTrash}
              onClick={() => deleteCustomer(customer.id)}
              tooltip={t('customers:deleteCustomer')}
            />
          )}
        </div>
      ),
    },
  ];

  const breadcrumbs = [
    { label: t('customers:breadcrumbs.customers') }
  ];

  return (
    <PageLayout
      title={t('customers:title')}
      subtitle={t('customers:subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={customers}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={true}
        onExport={handleExport}
        refreshable={true}
        addButton={hasPermission("Create Customer") ? {
          label: t('customers:addNew'),
          onClick: handleCreateCustomer
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters(prev => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onDateRangeChange={(key, range) => {
          setDateFilters(prev => ({ ...prev, [key]: range }));
          setCurrentPage(1);
        }}
        onRefresh={() => fetchCustomers(currentPage, pageSize)}
        emptyMessage={t('customers:messages.noCustomers')}
      />
    </PageLayout>
  );
};
